import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import * as monetization from '../services/monetization';
import { fetchStreak, claimStreakMilestone, buyStreakFreeze, recoverStreak } from '../services/api';
import { useAuth } from './AuthContext';
import { trackEvent } from '../utils/analytics';
import { t } from '../i18n';

const MonetizationContext = createContext();

// Minimum interval between ad refreshes (30 seconds for AdSense compliance)
const MIN_AD_REFRESH_INTERVAL = 30000;

// Feature flag: disable all paid purchase UI (set via REACT_APP_PURCHASES_ENABLED)
const PURCHASES_ENABLED = process.env.REACT_APP_PURCHASES_ENABLED !== 'false';

export function useMonetization() {
    const context = useContext(MonetizationContext);
    if (!context) {
        throw new Error('useMonetization must be used within a MonetizationProvider');
    }
    return context;
}

export function MonetizationProvider({ children }) {
    // Get auth context
    const { isAuthenticated, setShowLoginPrompt, setLoginPromptMessage } = useAuth();

    // Wallet state
    const [hintBalance, setHintBalance] = useState(0);
    const [adsRemoved, setAdsRemoved] = useState(false);

    // Hint status for current game
    const [freeHintsRemaining, setFreeHintsRemaining] = useState(0);
    const [freeHintsUsed, setFreeHintsUsed] = useState(0);
    const [canUseFreeHint, setCanUseFreeHint] = useState(false);
    const [hasAdHintAvailable, setHasAdHintAvailable] = useState(false);
    const hasAdHintAvailableRef = useRef(false);
    const [needsUnlock, setNeedsUnlock] = useState(false);

    // UI state
    const [showHintUnlockModal, setShowHintUnlockModal] = useState(false);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [purchaseLoading, setPurchaseLoading] = useState(false);

    // Keep ref in sync for use in useHint's stable callback
    useEffect(() => {
        hasAdHintAvailableRef.current = hasAdHintAvailable;
    }, [hasAdHintAvailable]);

    // Ad refresh state
    const [adRefreshTrigger, setAdRefreshTrigger] = useState(0);
    const lastAdRefreshTime = useRef(0);

    // Streak state
    const [streakData, setStreakData] = useState(null);

    // Fetch wallet data on mount
    const refreshWallet = useCallback(async () => {
        try {
            const response = await monetization.getWallet();
            if (response.success) {
                setHintBalance(response.wallet.hintBalance);
                setAdsRemoved(response.wallet.adsRemoved);
            }
        } catch (error) {
            console.error('Error fetching wallet:', error);
        }
    }, []);

    // Fetch hint status for current game
    const refreshHintStatus = useCallback(async () => {
        try {
            const response = await monetization.getHintStatus();
            if (response.success) {
                const status = response.hintStatus;
                setFreeHintsRemaining(status.freeHintsRemaining);
                setFreeHintsUsed(status.freeHintsUsed);
                setHintBalance(status.hintBalance);
                setAdsRemoved(status.adsRemoved);
                setCanUseFreeHint(status.canUseFreeHint);
                setHasAdHintAvailable(status.hasAdHintAvailable || false);
                setNeedsUnlock(status.needsUnlock);
            }
        } catch (error) {
            console.error('Error fetching hint status:', error);
        }
    }, []);

    // Fetch streak data
    const refreshStreak = useCallback(async () => {
        try {
            const response = await fetchStreak();
            if (response.success) {
                setStreakData(response.streak);
            }
        } catch (error) {
            console.error('Error fetching streak:', error);
        }
    }, []);

    // Initialize on mount - all three fetches in parallel
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await Promise.all([refreshWallet(), refreshHintStatus(), refreshStreak()]);
            setLoading(false);
        };
        init();
    }, [refreshWallet, refreshHintStatus, refreshStreak]);

    // Re-fetch all monetization data when user logs in (data may have been migrated)
    const prevAuthRef = useRef(isAuthenticated);
    useEffect(() => {
        if (isAuthenticated && !prevAuthRef.current) {
            refreshWallet();
            refreshHintStatus();
            refreshStreak();
        }
        prevAuthRef.current = isAuthenticated;
    }, [isAuthenticated, refreshWallet, refreshHintStatus, refreshStreak]);

    /**
     * Attempt to use a hint for the current game.
     * Returns { success, hintGranted, hintType, needsUnlock }
     */
    const useHint = useCallback(async () => {
        try {
            const response = await monetization.useHint();

            if (response.success) {
                setHintBalance(response.hintBalance);
                setFreeHintsRemaining(response.freeHintsRemaining);
                // Update ad hint availability from response
                if (response.adHintsAvailable !== undefined) {
                    setHasAdHintAvailable(response.adHintsAvailable > 0);
                }

                if (response.hintGranted) {
                    // Hint was used successfully
                    if (response.hintType === 'free') {
                        setFreeHintsUsed(prev => prev + 1);
                        setCanUseFreeHint(response.freeHintsRemaining > 0);
                    } else if (response.hintType === 'ad') {
                        // Ad hint was consumed
                        setHasAdHintAvailable(response.adHintsAvailable > 0);
                    }
                    return {
                        success: true,
                        hintGranted: true,
                        hintType: response.hintType,
                    };
                } else {
                    // Server says no hints available.
                    // If we have a locally-granted ad hint (user watched ad but
                    // server didn't record it), honor the local state.
                    if (hasAdHintAvailableRef.current) {
                        setHasAdHintAvailable(false);
                        return {
                            success: true,
                            hintGranted: true,
                            hintType: 'ad',
                        };
                    }
                    // Truly no hints available - show unlock modal
                    setNeedsUnlock(true);
                    setAdsRemoved(response.adsRemoved);
                    setShowHintUnlockModal(true);
                    return {
                        success: true,
                        hintGranted: false,
                        needsUnlock: true,
                    };
                }
            }

            // Server call returned but not success — check local fallback
            if (hasAdHintAvailableRef.current) {
                setHasAdHintAvailable(false);
                return {
                    success: true,
                    hintGranted: true,
                    hintType: 'ad',
                };
            }
            return { success: false, error: t('store.errors.useHintFailed') };
        } catch (error) {
            console.error('Error using hint:', error);
            // Network error — check local fallback
            if (hasAdHintAvailableRef.current) {
                setHasAdHintAvailable(false);
                return {
                    success: true,
                    hintGranted: true,
                    hintType: 'ad',
                };
            }
            return { success: false, error: error.message };
        }
    }, []);

    /**
     * Get a server-issued challenge token for a web rewarded ad. Returns
     * null when the request fails — the caller should then fall back to
     * AdMob SSV (native) or skip the reward.
     */
    const getAdChallenge = useCallback(async () => {
        try {
            const response = await monetization.getAdChallenge();
            return response?.challenge || null;
        } catch (error) {
            console.warn('Failed to fetch ad challenge:', error);
            return null;
        }
    }, []);

    /**
     * Grant a hint after watching an ad. For web rewarded ads pass the
     * challenge token issued by getAdChallenge() before the ad started.
     * The server is the sole authority on whether the reward is granted —
     * if the server refuses (invalid/expired challenge, rate limited), we
     * surface that to the caller instead of silently overriding.
     */
    const grantAdReward = useCallback(async (
        adType = 'rewarded_video', adUnitId = '', challenge = null
    ) => {
        const grantLocally = () => {
            setShowHintUnlockModal(false);
            setNeedsUnlock(false);
            setHasAdHintAvailable(true);
            hasAdHintAvailableRef.current = true;
        };

        try {
            const response = await monetization.grantAdReward(adType, adUnitId, challenge);

            if (response.success && response.hintGranted) {
                grantLocally();
                if (response.hintBalance !== undefined) {
                    setHintBalance(response.hintBalance);
                }
                trackEvent('Ad Reward', { ad_type: adType, reward_type: 'hint' });
                return { success: true, hintGranted: true };
            }

            return {
                success: false,
                hintGranted: false,
                error: response?.error || t('store.errors.adRewardNotVerified'),
            };
        } catch (error) {
            return {
                success: false,
                hintGranted: false,
                error: error?.message || t('store.errors.adRewardFailed'),
            };
        }
    }, []);

    /**
     * Purchase hints or remove ads using Stripe
     */
    const purchaseWithStripe = useCallback(async (purchaseType, stripe, elements, cardElement) => {
        setPurchaseLoading(true);

        try {
            // Create PaymentIntent
            const intentResponse = await monetization.createPaymentIntent(purchaseType);

            if (!intentResponse.success) {
                setPurchaseLoading(false);
                return { success: false, error: intentResponse.error || t('store.errors.createPaymentFailed') };
            }

            // For mock payments (development) - no Stripe needed
            if (intentResponse.mock) {
                const confirmResponse = await monetization.confirmPayment(
                    'mock_payment',
                    purchaseType
                );

                if (confirmResponse.success) {
                    setHintBalance(confirmResponse.hintBalance);
                    setAdsRemoved(confirmResponse.adsRemoved);
                    setShowPurchaseModal(false);
                    setShowHintUnlockModal(false);
                    setNeedsUnlock(false);
                    return { success: true, mock: true };
                }

                return { success: false, error: t('store.errors.paymentFailed') };
            }

            // Real Stripe payment - need stripe and cardElement
            if (!stripe || !cardElement) {
                setPurchaseLoading(false);
                return { success: false, error: t('store.errors.stripeNotLoaded') };
            }

            // Confirm card payment with Stripe
            const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
                intentResponse.clientSecret,
                {
                    payment_method: {
                        card: cardElement,
                    },
                }
            );

            if (stripeError) {
                setPurchaseLoading(false);
                return { success: false, error: stripeError.message };
            }

            if (paymentIntent && paymentIntent.status === 'succeeded') {
                // Confirm with our backend
                const confirmResponse = await monetization.confirmPayment(
                    paymentIntent.id,
                    purchaseType
                );

                if (confirmResponse.success) {
                    setHintBalance(confirmResponse.hintBalance);
                    setAdsRemoved(confirmResponse.adsRemoved);
                    setShowPurchaseModal(false);
                    setShowHintUnlockModal(false);
                    setNeedsUnlock(false);
                    setPurchaseLoading(false);
                    // Purchase is tracked server-side now (reliable + ad-blocker-
                    // proof) in complete_purchase() -> Plausible, with revenue.
                    return { success: true };
                }

                setPurchaseLoading(false);
                return { success: false, error: t('store.errors.verifyFailed') };
            }

            setPurchaseLoading(false);
            return { success: false, error: t('store.errors.paymentNotCompleted') };
        } catch (error) {
            console.error('Purchase error:', error);
            setPurchaseLoading(false);
            return { success: false, error: error.message };
        }
    }, []);

    /**
     * Purchase using native In-App Purchase (iOS/Android)
     */
    const purchaseWithIAP = useCallback(async (productId) => {
        setPurchaseLoading(true);

        try {
            const platform = monetization.getPlatform();

            // Check if Capacitor purchases plugin is available
            if (!window.Capacitor?.Plugins?.Purchases) {
                return { success: false, error: t('store.errors.iapUnavailable') };
            }

            const { Purchases } = window.Capacitor.Plugins;

            // RevenueCat v9: fetch product first, then purchase
            const { products } = await Purchases.getProducts({ productIdentifiers: [productId] });
            if (!products || products.length === 0) {
                return { success: false, error: t('store.errors.productNotFound') };
            }

            const { customerInfo, transaction } = await Purchases.purchaseStoreProduct({ product: products[0] });

            if (transaction) {
                // Try to get the app receipt for server verification
                let receiptData = '';
                try {
                    if (platform === 'apple' && window.Capacitor?.Plugins?.Purchases) {
                        const { appUserID } = await Purchases.getAppUserID();
                        // Get receipt from StoreKit
                        const { receiptString } = await Purchases.getAppStoreReceipt?.() || {};
                        receiptData = receiptString || '';
                    }
                } catch (e) {
                    console.warn('Could not fetch receipt:', e);
                }

                // Verify with our backend
                const verifyResponse = await monetization.verifyIAP(
                    platform,
                    productId,
                    receiptData,
                    transaction.transactionIdentifier || transaction.transactionId || ''
                );

                if (verifyResponse.success) {
                    setHintBalance(verifyResponse.hintBalance);
                    setAdsRemoved(verifyResponse.adsRemoved);
                    setShowPurchaseModal(false);
                    setShowHintUnlockModal(false);
                    setNeedsUnlock(false);
                    // Purchase is tracked server-side now (reliable on the native
                    // IAP path too) in complete_purchase() -> Plausible.
                    return { success: true };
                }
            }

            return { success: false, error: t('store.errors.purchaseNotCompleted') };
        } catch (error) {
            console.error('IAP error:', error);
            return { success: false, error: error.message };
        } finally {
            setPurchaseLoading(false);
        }
    }, []);

    /**
     * Restore previous purchases (for iOS)
     */
    const restorePurchases = useCallback(async () => {
        setPurchaseLoading(true);

        try {
            const platform = monetization.getPlatform();
            const response = await monetization.restorePurchases(platform);

            if (response.success) {
                setHintBalance(response.hintBalance);
                setAdsRemoved(response.adsRemoved);

                if (response.restored && response.restored.length > 0) {
                    return { success: true, restored: response.restored };
                }

                return { success: true, restored: [] };
            }

            return { success: false, error: t('store.errors.restoreFailed') };
        } catch (error) {
            console.error('Restore error:', error);
            return { success: false, error: error.message };
        } finally {
            setPurchaseLoading(false);
        }
    }, []);

    // Claim a streak milestone reward
    const claimMilestone = useCallback(async (milestone) => {
        try {
            const response = await claimStreakMilestone(milestone);
            if (response.success) {
                setHintBalance(response.hintBalance);
                // Update claimable list
                setStreakData(prev => prev ? {
                    ...prev,
                    claimableMilestones: response.claimableMilestones || [],
                } : prev);
                trackEvent('Streak Milestone', { days: String(milestone) });
                return { success: true, hintsAwarded: response.hintsAwarded };
            }
            return { success: false, error: response.error };
        } catch (error) {
            console.error('Error claiming milestone:', error);
            return { success: false, error: error.message };
        }
    }, []);

    // Buy streak freeze with hints
    const buyFreezeWithHints = useCallback(async (count = 1) => {
        try {
            const response = await buyStreakFreeze(count);
            if (response.success) {
                setHintBalance(response.hintBalance);
                setStreakData(prev => prev ? {
                    ...prev,
                    freezeCount: response.freezeCount,
                } : prev);
                return { success: true };
            }
            return { success: false, error: response.error };
        } catch (error) {
            console.error('Error buying freeze:', error);
            return { success: false, error: error.message };
        }
    }, []);

    // Recover lost streak with hints
    const recoverStreakWithHints = useCallback(async () => {
        try {
            const response = await recoverStreak();
            if (response.success) {
                setHintBalance(response.hintBalance);
                setStreakData(prev => prev ? {
                    ...prev,
                    currentStreak: response.currentStreak,
                    longestStreak: response.longestStreak,
                    recovery: null,
                } : prev);
                return { success: true };
            }
            return { success: false, error: response.error };
        } catch (error) {
            console.error('Error recovering streak:', error);
            return { success: false, error: error.message };
        }
    }, []);

    // Reset hint status when game changes
    const resetGameHintStatus = useCallback(() => {
        setFreeHintsRemaining(0);
        setFreeHintsUsed(0);
        setCanUseFreeHint(false);
        setHasAdHintAvailable(false);
        setNeedsUnlock(false);
        refreshHintStatus();
    }, [refreshHintStatus]);

    // Open modals
    const openHintUnlockModal = useCallback(() => {
        setShowHintUnlockModal(true);
    }, []);

    const closeHintUnlockModal = useCallback(() => {
        setShowHintUnlockModal(false);
    }, []);

    const openPurchaseModal = useCallback(() => {
        if (!PURCHASES_ENABLED) return;
        if (!isAuthenticated) {
            setLoginPromptMessage(t('store.loginPrompt'));
            setShowLoginPrompt(true);
            return;
        }
        setShowPurchaseModal(true);
    }, [isAuthenticated, setLoginPromptMessage, setShowLoginPrompt]);

    const closePurchaseModal = useCallback(() => {
        setShowPurchaseModal(false);
    }, []);

    /**
     * Refresh banner ads (respects minimum 30-second interval)
     * Call this on level completion, mode switch, etc.
     */
    const refreshAds = useCallback(() => {
        // Don't refresh if ads are removed
        if (adsRemoved) return;

        const now = Date.now();
        const timeSinceLastRefresh = now - lastAdRefreshTime.current;

        // Respect minimum interval (30 seconds for AdSense compliance)
        if (timeSinceLastRefresh < MIN_AD_REFRESH_INTERVAL) {
            console.log(`Ad refresh skipped - only ${Math.round(timeSinceLastRefresh / 1000)}s since last refresh`);
            return;
        }

        lastAdRefreshTime.current = now;
        setAdRefreshTrigger(prev => prev + 1);
        console.log('Ad refresh triggered');
    }, [adsRemoved]);

    const value = {
        // Wallet state
        hintBalance,
        adsRemoved,

        // Hint status
        freeHintsRemaining,
        freeHintsUsed,
        canUseFreeHint,
        hasAdHintAvailable,
        needsUnlock,

        // Loading states
        loading,
        purchaseLoading,

        // Modal state
        showHintUnlockModal,
        showPurchaseModal,

        // Actions
        refreshWallet,
        refreshHintStatus,
        useHint,
        getAdChallenge,
        grantAdReward,
        purchaseWithStripe,
        purchaseWithIAP,
        restorePurchases,
        resetGameHintStatus,
        refreshAds,

        // Ad refresh trigger (increment to trigger refresh in AdBanner)
        adRefreshTrigger,

        // Modal controls
        openHintUnlockModal,
        closeHintUnlockModal,
        openPurchaseModal,
        closePurchaseModal,

        // Streak
        streakData,
        refreshStreak,
        claimMilestone,
        buyFreezeWithHints,
        recoverStreakWithHints,

        // Feature flag
        purchasesEnabled: PURCHASES_ENABLED,

        // Utilities
        isNativeMobile: monetization.isNativeMobile,
        getPlatform: monetization.getPlatform,
        PURCHASE_OPTIONS: monetization.PURCHASE_OPTIONS,
    };

    return (
        <MonetizationContext.Provider value={value}>
            {children}
        </MonetizationContext.Provider>
    );
}

export default MonetizationContext;
